import React, { useEffect, useRef, useState } from 'react';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import { galleries, videoGallery } from '../mock';
import { ChevronLeft, ChevronRight, Play } from 'lucide-react';

const Gallery = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [visibleVideos, setVisibleVideos] = useState(6);
  const thumbnailStripRef = useRef(null);
  const thumbnailRefs = useRef([]);

  const photos = Object.values(galleries)
    .flat()
    .filter(photo => photo.url && !photo.url.includes('/download/video/'));
  const currentPhoto = photos[currentSlide];

  const goToSlide = index => {
    setCurrentSlide((index + photos.length) % photos.length);
  };

  const goToPrevious = () => {
    setCurrentSlide(index => (index === 0 ? photos.length - 1 : index - 1));
  };

  const goToNext = () => {
    setCurrentSlide(index => (index === photos.length - 1 ? 0 : index + 1));
  };

  useEffect(() => {
    const activeThumbnail = thumbnailRefs.current[currentSlide];
    if (!activeThumbnail) return;

    activeThumbnail.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [currentSlide]);

  useEffect(() => {
    const handleKeyPress = event => {
      if (
        event.target instanceof HTMLElement &&
        (event.target.isContentEditable ||
          ['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target.tagName))
      ) {
        return;
      }

      if (event.key === 'ArrowLeft') goToPrevious();
      if (event.key === 'ArrowRight') goToNext();
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [photos.length]);

  const loadMoreVideos = () => setVisibleVideos(count => count + 6);

  return (
    <>
      <SEO
        title="Photography Portfolio in Lucknow | Frames by Gaurav"
        description="Browse wedding, event, fashion and commercial photography by Frames by Gaurav in Lucknow, including selected photographs and films."
        keywords="wedding photography gallery, event photography portfolio, fashion photography lucknow, photography portfolio, wedding photographer gallery lucknow, frames by gaurav portfolio"
        canonicalUrl="/gallery"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Gallery - Frames by Gaurav',
          description:
            'Browse wedding, event, fashion and commercial photography by Frames by Gaurav in Lucknow.',
          url: 'https://www.framesbygaurav.com/gallery',
        }}
      />
      <Layout>
        <main>
          <section
            aria-label="Photography slideshow"
            className="relative mt-20 h-[calc(100vh-5rem)] min-h-[460px] max-h-[920px] w-full overflow-hidden bg-black"
          >
            <img
              key={currentPhoto.id}
              src={currentPhoto.url}
              alt={`${currentPhoto.category}: ${currentPhoto.title}`}
              className="absolute inset-0 h-full w-full object-cover transition-opacity duration-500"
              fetchPriority="high"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-black/15" />

            <div className="absolute left-6 top-6 z-10 rounded-full border border-white/35 bg-black/30 px-4 py-2 text-xs tracking-widest text-white backdrop-blur-sm md:left-10 md:top-10">
              {String(currentSlide + 1).padStart(2, '0')} /{' '}
              {String(photos.length).padStart(2, '0')}
            </div>

            <button
              type="button"
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 z-10 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full border border-white/60 bg-black/25 text-white backdrop-blur-sm transition-colors hover:bg-white hover:text-black md:left-10 md:h-14 md:w-14"
              aria-label="Previous photo"
            >
              <ChevronLeft size={26} />
            </button>
            <button
              type="button"
              onClick={goToNext}
              className="absolute right-4 top-1/2 z-10 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full border border-white/60 bg-black/25 text-white backdrop-blur-sm transition-colors hover:bg-white hover:text-black md:right-10 md:h-14 md:w-14"
              aria-label="Next photo"
            >
              <ChevronRight size={26} />
            </button>

            <div className="absolute inset-x-0 bottom-0 z-10 px-6 pb-8 pt-28 text-white md:px-14 md:pb-12">
              <p className="mb-2 text-xs uppercase tracking-[0.18em] text-white/75">
                {currentPhoto.category}
              </p>
              <h1 className="text-3xl md:text-5xl font-bold">
                {currentPhoto.title}
              </h1>
              {currentPhoto.tagline && (
                <p className="mt-2 text-sm text-white/85 md:text-base">
                  {currentPhoto.tagline}
                </p>
              )}
            </div>
          </section>

          <section
            aria-label="Photo previews"
            className="bg-white py-5 md:py-7"
          >
            <div
              ref={thumbnailStripRef}
              className="overflow-x-auto scrollbar-hide scroll-smooth"
            >
              <div className="mx-auto flex w-max gap-2 px-5 md:gap-3 md:px-8">
                {photos.map((photo, index) => (
                  <button
                    key={`${photo.id}-${index}`}
                    ref={element => {
                      thumbnailRefs.current[index] = element;
                    }}
                    type="button"
                    onClick={() => goToSlide(index)}
                    className={`h-14 w-20 flex-shrink-0 overflow-hidden border transition-opacity md:h-[72px] md:w-28 ${
                      currentSlide === index
                        ? 'border-black opacity-100'
                        : 'border-transparent opacity-50 hover:opacity-90'
                    }`}
                    aria-label={`Preview photo ${index + 1}: ${photo.title}`}
                    aria-current={currentSlide === index ? 'true' : undefined}
                    aria-pressed={currentSlide === index}
                  >
                    <img
                      src={photo.url}
                      alt=""
                      className="h-full w-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </button>
                ))}
              </div>
            </div>
          </section>

          {videoGallery.length > 0 && (
            <section className="bg-gray-50 py-16 md:py-20">
              <div className="mx-auto max-w-7xl px-6">
                <h2 className="mb-6 text-center text-4xl font-bold tracking-tight md:text-5xl">
                  Watch Our Work
                </h2>
                <p className="mx-auto mb-12 max-w-2xl text-center text-lg text-gray-600">
                  Experience our storytelling through cinematic wedding films
                  and photography showcases.
                </p>

                <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                  {videoGallery.slice(0, visibleVideos).map(video => (
                    <a
                      key={video.id}
                      href={`https://www.youtube.com/watch?v=${String(video.youtubeId).split('&')[0]}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group"
                    >
                      <div className="relative aspect-video overflow-hidden bg-black">
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                          decoding="async"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/35 transition-colors group-hover:bg-black/50">
                          <span className="grid h-16 w-16 place-items-center rounded-full bg-white text-black transition-transform group-hover:scale-110">
                            <Play size={26} fill="currentColor" />
                          </span>
                        </div>
                      </div>
                      <h3 className="mt-4 text-lg font-bold text-black">
                        {video.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-gray-600">
                        {video.description}
                      </p>
                    </a>
                  ))}
                </div>

                {visibleVideos < videoGallery.length && (
                  <div className="mt-12 text-center">
                    <button
                      type="button"
                      onClick={loadMoreVideos}
                      className="bg-black px-10 py-4 text-sm uppercase tracking-widest text-white transition-colors hover:bg-gray-800"
                    >
                      Load More Videos
                    </button>
                  </div>
                )}

                <div className="mt-12 text-center">
                  <a
                    href="https://youtube.com/@ajayrajproductionslko?si=A1oroojXoB2YfNYA"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block border border-black px-8 py-3 text-sm uppercase tracking-widest text-black transition-colors hover:bg-black hover:text-white"
                  >
                    Watch More on YouTube
                  </a>
                </div>
              </div>
            </section>
          )}
        </main>
      </Layout>
    </>
  );
};

export default Gallery;
